import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../utils/api';
import { setStoredToken } from '../utils/api';

const USER_KEY = 'auth_user';

function getStoredUser(): User | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function setStoredUser(user: User | null) {
  if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
  else localStorage.removeItem(USER_KEY);
}

interface User { id: number; email: string; name?: string; role: string; phone?: string; }
interface AuthCtx {
  user: User | null;
  loading: boolean;
  login:    (email: string, password: string) => Promise<{ ok: boolean; error?: string }>;
  register: (email: string, password: string, extra?: { name?: string; phone?: string; dob?: string }) => Promise<{ ok: boolean; error?: string }>;
  logout:   () => Promise<void>;
  updateProfile: (data: { name?: string; phone?: string }) => Promise<{ ok: boolean; error?: string }>;
  changePassword: (current: string, next: string) => Promise<{ ok: boolean; error?: string }>;
}

const AuthContext = createContext<AuthCtx | null>(null);
export const useAuth = () => useContext(AuthContext)!;

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser]       = useState<User | null>(getStoredUser);
  const [loading, setLoading] = useState(true);

  // Restore session on mount — validate stored session with the server
  useEffect(() => {
    api.get<User>('/auth/me')
      .then(d => { setUser(d); setStoredUser(d); })
      .catch(() => {
        // Server rejected — only clear if we have no stored token fallback
        // Keep the stored user if the token is still present (server may be temporarily down)
      })
      .finally(() => setLoading(false));
  }, []);

  // Global 401 handler — any API call (except /auth/me) that gets a 401
  // dispatches 'auth:logout'. We listen here and clear the user so
  // Protected routes redirect to login without showing error toasts.
  useEffect(() => {
    const onUnauthorized = () => {
      setUser(null);
      setStoredUser(null);
      setStoredToken(null);
    };
    window.addEventListener('auth:logout', onUnauthorized);
    return () => window.removeEventListener('auth:logout', onUnauthorized);
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const d = await api.post<User & { token?: string }>('/auth/login', { email, password });
      if (d.token) setStoredToken(d.token);
      const userData: User = { id: d.id, email: d.email, name: d.name, role: d.role, phone: d.phone };
      setUser(userData);
      setStoredUser(userData);
      return { ok: true };
    } catch (e: any) { return { ok: false, error: e.message || 'Login failed' }; }
  };

  const register = async (email: string, password: string, extra?: { name?: string; phone?: string; dob?: string }) => {
    try {
      const d = await api.post<(User & { token?: string }) | { message: string }>('/auth/register', { email, password, ...extra });
      if ('token' in d && d.token) setStoredToken(d.token);
      if ('id' in d) {
        const userData: User = { id: d.id, email: d.email, name: d.name, role: d.role, phone: d.phone };
        setUser(userData);
        setStoredUser(userData);
      }
      return { ok: true };
    } catch (e: any) { return { ok: false, error: e.message || 'Registration failed' }; }
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch {}
    setUser(null);
    setStoredUser(null);
    setStoredToken(null);
  };

  const updateProfile = async (data: { name?: string; phone?: string }) => {
    try {
      const d = await api.put<Partial<User>>('/auth/profile', data);
      setUser(u => {
        const updated = u ? { ...u, ...d } : u;
        if (updated) setStoredUser(updated);
        return updated;
      });
      return { ok: true };
    } catch (e: any) { return { ok: false, error: e.message || 'Update failed' }; }
  };

  const changePassword = async (current: string, next: string) => {
    try {
      await api.post('/auth/change-password', { current, next }); return { ok: true };
    } catch (e: any) { return { ok: false, error: e.message || 'Password change failed' }; }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateProfile, changePassword }}>
      {children}
    </AuthContext.Provider>
  );
};
